/**
 * Client-side image compression — reduces file size before upload without
 * compromising perceived quality. Uses Canvas: downscale (preserving aspect
 * ratio) + re-encode as JPEG/WEBP at high quality.
 *
 *   compressImage(file, { maxSide: 1600, quality: 0.82 })
 *     → Promise<File>  (always a File so it slots into FormData unchanged)
 *
 * - If browser doesn't support what we need, returns the original file untouched.
 * - PNG with transparency is encoded as WEBP (preserves alpha + smaller).
 * - SVG / GIF are passed through untouched (animations / vectors).
 */
export async function compressImage(file, opts = {}) {
  const {
    maxSide = 1600,
    quality = 0.82,
    minBytesToCompress = 60 * 1024, // skip tiny files
  } = opts;

  if (!(file instanceof Blob)) return file;
  if (file.size < minBytesToCompress) return file;
  if (!/^image\/(jpeg|jpg|png|webp|heic|heif)$/i.test(file.type)) return file;

  let bitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return file; // unsupported in browser — fall through
  }

  const { width, height } = bitmap;
  const longest = Math.max(width, height);
  const scale = longest > maxSide ? maxSide / longest : 1;

  // If no resize AND already-small re-encode would only marginally help → skip
  if (scale === 1 && file.size < 400 * 1024) return file;

  const targetW = Math.round(width * scale);
  const targetH = Math.round(height * scale);

  const canvas = document.createElement('canvas');
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext('2d');
  if (!ctx) return file;
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(bitmap, 0, 0, targetW, targetH);

  // PNG → WEBP to keep transparency but cut bytes; everything else → JPEG.
  const isTransparent = /png/i.test(file.type);
  const mime = isTransparent ? 'image/webp' : 'image/jpeg';
  const ext = isTransparent ? 'webp' : 'jpg';

  const blob = await new Promise((resolve) => canvas.toBlob(resolve, mime, quality));
  if (!blob) return file;
  if (blob.size >= file.size) return file; // worse — keep original

  // Preserve a friendly filename for the server
  const base = (file.name || 'image').replace(/\.[^.]+$/, '');
  return new File([blob], `${base}.${ext}`, { type: mime, lastModified: Date.now() });
}

/** Compact "Before → After" string for toast feedback */
export function formatCompressionStats(before, after) {
  const fmt = (b) => b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(2)} MB` : `${Math.round(b / 1024)} KB`;
  const pct = Math.max(0, Math.round((1 - after / before) * 100));
  return `${fmt(before)} → ${fmt(after)} (${pct}% smaller)`;
}
